import React, { useState } from 'react';
import { useAuth } from '../contexts/EnhancedAuthContext';
import { Eye, EyeOff, Loader2, Mail, Lock } from 'lucide-react';

interface LoginFormProps {
  onSwitchToSignUp?: () => void;
  onSwitchToForgotPassword?: () => void;
}

interface LoginFormFieldsProps {
  email: string;
  password: string;
  showPassword: boolean;
  isMagicLink: boolean;
  isSubmitting: boolean;
  onEmailChange: (value: string) => void;
  onPasswordChange: (value: string) => void;
  onTogglePassword: () => void;
}

/* eslint-disable max-lines-per-function */
const LoginFormFields: React.FC<LoginFormFieldsProps> = ({
  email,
  password,
  showPassword,
  isMagicLink,
  isSubmitting,
  onEmailChange,
  onPasswordChange,
  onTogglePassword
}) => (
  <>
    <div>
      <label htmlFor='email' className='block text-sm font-medium text-gray-900 mb-2'>
        Email address
      </label>
      <div className='relative'>
        <input
          id='email'
          type='email'
          value={email}
          onChange={e => onEmailChange(e.target.value)}
          placeholder='Enter your email'
          className={[
            'w-full px-3 py-2.5 pl-10 border border-gray-300 bg-white',
            'text-gray-900 rounded-md focus:outline-none focus:ring-2',
            'focus:ring-[#d4854a] focus:border-transparent',
            'placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed'
          ].join(' ')}
          required
          disabled={isSubmitting}
          autoComplete='email'
        />
        <Mail
          className='absolute left-3 top-1/2 transform -translate-y-1/2
          h-5 w-5 text-gray-400'
        />
      </div>
    </div>

    {!isMagicLink && (
      <div>
        <label htmlFor='password' className='block text-sm font-medium text-gray-900 mb-2'>
          Password
        </label>
        <div className='relative'>
          <input
            id='password'
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => onPasswordChange(e.target.value)}
            placeholder='Enter your password'
            className={[
              'w-full px-3 py-2.5 pl-10 pr-10 border border-gray-300 bg-white',
              'text-gray-900 rounded-md focus:outline-none focus:ring-2',
              'focus:ring-[#d4854a] focus:border-transparent',
              'placeholder:text-gray-400 disabled:opacity-50 disabled:cursor-not-allowed'
            ].join(' ')}
            required
            disabled={isSubmitting}
            autoComplete='current-password'
          />
          <Lock
            className='absolute left-3 top-1/2 transform -translate-y-1/2
            h-5 w-5 text-gray-400'
          />
          <button
            type='button'
            onClick={onTogglePassword}
            className='absolute right-3 top-1/2 transform -translate-y-1/2
              text-gray-500 hover:text-gray-700 focus:outline-none'
            disabled={isSubmitting}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOff className='h-5 w-5' /> : <Eye className='h-5 w-5' />}
          </button>
        </div>
      </div>
    )}
  </>
);

interface LoginFormActionsProps {
  isSubmitting: boolean;
  loading: boolean;
  isMagicLink: boolean;
  onForgotPassword?: () => void;
}

const LoginFormActions: React.FC<LoginFormActionsProps> = ({
  isSubmitting,
  loading,
  isMagicLink,
  onForgotPassword
}) => (
  <>
    <div className='flex items-center justify-between'>
      <label className='flex items-center space-x-2 cursor-pointer'>
        <input
          type='checkbox'
          className='w-4 h-4 rounded border-gray-300 text-[#d4854a]
            focus:ring-[#d4854a]'
          disabled={isSubmitting}
        />
        <span className='text-sm text-gray-600'>Remember for 30 days</span>
      </label>

      {!isMagicLink && onForgotPassword && (
        <button
          type='button'
          onClick={onForgotPassword}
          className='text-sm text-[#d4854a] hover:text-[#c17640] font-medium'
          disabled={isSubmitting}
        >
          Forgot password
        </button>
      )}
    </div>

    <button
      type='submit'
      className='w-full bg-[#d4854a] hover:bg-[#c17640] text-white
        font-semibold py-3 px-4 rounded-md transition-colors
        disabled:opacity-50 disabled:cursor-not-allowed'
      disabled={isSubmitting || loading}
    >
      {isSubmitting ? (
        <span className='flex items-center justify-center'>
          <Loader2 className='mr-2 h-4 w-4 animate-spin' />
          {isMagicLink ? 'Sending magic link...' : 'Signing in...'}
        </span>
      ) : isMagicLink ? (
        'Send magic link'
      ) : (
        'Sign in'
      )}
    </button>
  </>
);

/* eslint-disable max-lines-per-function */
export const LoginForm: React.FC<LoginFormProps> = ({
  onSwitchToSignUp: _onSwitchToSignUp,
  onSwitchToForgotPassword
}) => {
  const { signIn, signInWithMagicLink, loading, error } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isMagicLink] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      if (isMagicLink) {
        await signInWithMagicLink(email);
      } else {
        await signIn(email, password);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='w-full max-w-md'>
      <div className='mb-4 sm:mb-6 md:mb-8'>
        <h1 className='text-2xl sm:text-3xl font-bold text-gray-900 mb-2'>Welcome back</h1>
        <p className='text-sm sm:text-base text-gray-600'>Please enter your details</p>
      </div>

      {error && (
        <div
          className='bg-red-50 border border-red-200 text-red-700 px-3 py-2
          sm:px-4 sm:py-3 rounded-md mb-3 sm:mb-4 text-sm'
        >
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className='space-y-3 sm:space-y-5'>
        <LoginFormFields
          email={email}
          password={password}
          showPassword={showPassword}
          isMagicLink={isMagicLink}
          isSubmitting={isSubmitting}
          onEmailChange={setEmail}
          onPasswordChange={setPassword}
          onTogglePassword={() => setShowPassword(!showPassword)}
        />
        <LoginFormActions
          isSubmitting={isSubmitting}
          loading={loading}
          isMagicLink={isMagicLink}
          {...(onSwitchToForgotPassword && { onForgotPassword: onSwitchToForgotPassword })}
        />
      </form>

      {isMagicLink && (
        <div className='mt-3 sm:mt-4 p-2.5 sm:p-3 bg-blue-50 border border-blue-200 rounded-md'>
          <p className='text-xs sm:text-sm text-blue-800'>
            We&apos;ll send you a secure link to sign in without a password.
          </p>
        </div>
      )}

      <div className='mt-4 sm:mt-6 text-center'>
        <p className='text-xs sm:text-sm text-gray-600'>
          Need an account? Contact your administrator to create one.
        </p>
      </div>
    </div>
  );
};
